import { motion, useScroll, useTransform } from "framer-motion";
import { Star } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { HERO_IMG, WHATSAPP_DEFAULT, scrollToId } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, index, className = "" }) {
    return (
        <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
            <motion.span
                className={`block ${className}`}
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.05, ease: EASE, delay: 0.25 + index * 0.16 }}
            >
                {children}
            </motion.span>
        </span>
    );
}

const STATS = [
    { value: "4.9", star: true, label: "Avaliação dos clientes" },
    { value: "+3.500", label: "Cortes realizados" },
    { value: "100%", label: "Satisfação garantida" },
];

export default function Hero() {
    const { scrollY } = useScroll();
    const bgY = useTransform(scrollY, [0, 900], [0, 220]);
    const bgScale = useTransform(scrollY, [0, 900], [1.08, 1.16]);
    const fade = useTransform(scrollY, [0, 500], [1, 0]);

    return (
        <section id="inicio" className="relative min-h-screen flex items-center overflow-hidden">
            <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
                <img
                    src={HERO_IMG}
                    alt="Interior da barbearia Corte Nobre"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/75 to-obsidian/25" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/60" />
            </motion.div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 w-full pt-36 pb-28">
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
                    className="flex items-center gap-3 mb-7"
                >
                    <span className="h-px w-12 bg-gold" />
                    <span className="text-gold text-[11px] sm:text-xs font-mono uppercase tracking-[0.35em]">
                        Barbearia clássica &amp; moderna — João Pessoa · PB
                    </span>
                </motion.div>

                <h1 className="font-display font-extrabold uppercase text-[13vw] sm:text-7xl lg:text-[86px] leading-[1.02] tracking-tight text-white">
                    <MaskedLine index={0}>A Arte da</MaskedLine>
                    <MaskedLine index={1}>Barba &amp; do</MaskedLine>
                    <MaskedLine index={2} className="text-gold-gradient">
                        Corte Nobre
                    </MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 22 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.95 }}
                    className="mt-7 max-w-xl text-cream/80 text-base sm:text-lg leading-relaxed"
                >
                    Tradição, sofisticação e precisão cirúrgica no coração de João Pessoa.
                    Experimente o verdadeiro ritual masculino.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 22 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: EASE, delay: 1.1 }}
                    className="mt-10 flex flex-col sm:flex-row gap-4"
                >
                    <a
                        href={WHATSAPP_DEFAULT}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="hero-whatsapp-cta"
                        className="group inline-flex items-center justify-center gap-3 bg-gold-gradient text-obsidian font-semibold uppercase tracking-[0.1em] text-sm px-9 py-4 rounded-full transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_35px_rgba(212,175,55,0.5)]"
                    >
                        <WhatsAppIcon size={19} className="group-hover:rotate-12 transition-transform duration-300" />
                        Agendar horário
                    </a>
                    <button
                        onClick={() => scrollToId("#servicos")}
                        data-testid="hero-services-cta"
                        className="inline-flex items-center justify-center gap-3 glass border border-gold/35 text-cream font-semibold uppercase tracking-[0.1em] text-sm px-9 py-4 rounded-full transition-all duration-300 hover:border-gold hover:text-gold hover:scale-[1.02]"
                    >
                        Ver tabela de serviços
                    </button>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.35 }}
                    className="mt-14 pt-8 border-t border-white/10 flex flex-wrap gap-x-14 gap-y-6"
                >
                    {STATS.map((s) => (
                        <div key={s.label}>
                            <div className="flex items-center gap-1.5 font-display text-3xl font-bold text-gold">
                                {s.value}
                                {s.star && (
                                    <Star size={20} className="fill-gold text-gold" />
                                )}
                            </div>
                            <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-cream/55">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </div>

            <motion.div
                style={{ opacity: fade }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2"
            >
                <span className="text-[10px] uppercase tracking-[0.3em] text-cream/50 font-mono">
                    Role
                </span>
                <div className="w-px h-10 bg-gradient-to-b from-gold to-transparent" />
            </motion.div>
        </section>
    );
}
